import React from 'react'
import { useTranslation } from 'react-i18next'

import { useNavigate } from '@lib/react-router-dom'

import { useModal } from '@context/ModalsContext'

import { ListItem } from '@ui/components/List'
import { Tooltip } from '@ui/components/Tooltip/Tooltip'
import { ExchangeIcon } from '@ui/icons/Exchange.icon'
import { InfoIcon } from '@ui/icons/Info.icon'
import { WithdrawalIcon } from '@ui/icons/Withdrawal.icon'

import { WalletRestrictions, isRestricted, isWalletSuspended } from '@utils/wallet.utils'

import { WalletDto } from '@model/WalletDto'

import { InformationModal } from '@shared/modals/Information.modal'

interface WalletBalancesOptionsCommonProps {
  wallet: WalletDto
  close: () => void
}

interface WalletBalancesWithdrawalOptionProps extends WalletBalancesOptionsCommonProps {
  onWithdrawal(walletId: string): void
  isLoading?: boolean
}

type WalletBalancesOptionsContentProps = WalletBalancesWithdrawalOptionProps &
  WalletBalancesOptionsCommonProps

export const WalletBalancesOptionsContent: React.FC<WalletBalancesOptionsContentProps> = (
  props
) => {
  const { wallet, onWithdrawal, isLoading, close } = props

  const { t } = useTranslation()
  const navigate = useNavigate()
  const walletInformationModal = useModal(InformationModal, {
    title: t('Wallet.Wallet Information'),
  })

  const hasAllRestrictions = isWalletSuspended(wallet.restrictions)
  const isWithdrawRestricted = isRestricted(WalletRestrictions.WITHDRAW, wallet.restrictions)
  const isConvertRestricted = isRestricted(WalletRestrictions.TRANSFER_FROM, wallet.restrictions)
  const hasNegativeWalletBalance = wallet.balance < 0
  const isWithdrawDisabled = isWithdrawRestricted || hasNegativeWalletBalance

  const withdrawTooltip = isWithdrawRestricted
    ? t('This action is currently unavailable.')
    : hasNegativeWalletBalance
    ? t('Withdrawals for this wallet are unavailable.')
    : null

  const tryWithdraw = () => {
    if (isWithdrawDisabled) return
    onWithdrawal(wallet.id)
    close()
  }

  const tryConvert = () => {
    if (isConvertRestricted) return
    navigate(`/dashboard/traders-room/transactions/conversion`, {
      state: { walletId: wallet.id },
    })
    close()
  }

  const openInfo = () => {
    walletInformationModal.open({ description: `${t('Wallet.Wallet number')}: ${wallet.name}` })
    close()
  }

  return (
    <>
      <Tooltip content={withdrawTooltip ?? ''} disabled={!withdrawTooltip}>
        <ListItem
          startSlot={{
            icon: WithdrawalIcon,
            pimple:
              isWithdrawRestricted && !hasAllRestrictions
                ? { bg: 'surface-primary-med-em' }
                : undefined,
          }}
          disabled={isWithdrawDisabled}
          onClick={tryWithdraw}
        >
          {isLoading ? t('Loading...') : t('Wallet.Withdraw')}
        </ListItem>
      </Tooltip>
      <Tooltip content={t('This action is currently unavailable.')} disabled={!isConvertRestricted}>
        <ListItem
          startSlot={{
            icon: ExchangeIcon,
            pimple:
              isConvertRestricted && !hasAllRestrictions
                ? { bg: 'surface-primary-med-em' }
                : undefined,
          }}
          disabled={isConvertRestricted}
          onClick={tryConvert}
        >
          {t('Wallet.Convert')}
        </ListItem>
      </Tooltip>
      <ListItem startSlot={{ icon: InfoIcon }} onClick={openInfo} size='lg'>
        {t('Wallet.Info')}
      </ListItem>
    </>
  )
}
